import { useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { pricingCatalogOptions } from "./query";
import { allowanceLabel } from "./catalog";

export function PricingPlans({ compact = false }: { compact?: boolean }) {
  const { data: catalog, refetch, isFetching } = useSuspenseQuery(pricingCatalogOptions());
  const [period, setPeriod] = useState<"monthly" | "annual">("monthly");
  if (!catalog)
    return (
      <div role="status" className="rounded-xl border border-hb-card p-6">
        <p>
          Current prices are temporarily unavailable. Please retry or contact support@bavimail.com.
        </p>
        <button
          type="button"
          disabled={isFetching}
          onClick={() => void refetch()}
          className="mt-4 text-hb-accent underline"
        >
          {isFetching ? "Loading prices…" : "Retry prices"}
        </button>
      </div>
    );
  const intervals = ["monthly", "annual"] as const;
  const available = intervals.filter((value) =>
    catalog.some((plan) => plan.billing_interval === value),
  );
  const selected = available.includes(period) ? period : available[0];
  const plans = catalog.filter(
    (plan) => !plan.billing_interval || plan.billing_interval === selected,
  );
  return (
    <div data-pricing-catalog>
      {available.length > 1 && (
        <div className="mb-6 flex gap-2" role="group" aria-label="Billing period">
          {available.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={selected === value}
              onClick={() => setPeriod(value)}
              className={`rounded-md border px-4 py-2 ${selected === value ? "border-hb-accent text-hb-accent" : "border-hb-card"}`}
            >
              {value === "annual" ? "Annual" : "Monthly"}
            </button>
          ))}
        </div>
      )}
      <div className="grid gap-5 md:grid-cols-3">
        {plans.map((plan) => (
          <section
            key={plan.id}
            className="flex flex-col rounded-xl border border-hb-card bg-hb-surface p-6"
          >
            <h3 className="text-xl font-bold">{plan.name}</h3>
            <p className="mt-4 text-3xl font-extrabold text-hb-accent">{plan.price_primary}</p>
            {plan.billing_interval && (
              <p className="mt-2 text-sm text-hb-muted">
                {plan.billing_interval === "annual" ? "Annual billing" : "Monthly billing"}
              </p>
            )}
            {plan.price_secondary && (
              <p className="mt-2 text-sm text-hb-muted">{plan.price_secondary}</p>
            )}
            <ul className="my-6 flex-1 space-y-3 text-sm">
              {(compact ? plan.items.slice(0, 2) : plan.items).map((item, index) => (
                <li key={index}>{allowanceLabel(item)}</li>
              ))}
            </ul>
            <Link
              to={compact ? "/pricing" : "/login"}
              preload="intent"
              className="rounded-md bg-hb-accent px-4 py-3 text-center font-bold text-hb-accent-fg"
            >
              {compact ? "View plan details" : "Get started"}
            </Link>
          </section>
        ))}
      </div>
    </div>
  );
}
